import React from "react";
import { Alert, Box, Button, Snackbar, Typography } from "@mui/material";
import { RefreshCw } from "lucide-react";
import { useRegisterSW } from "virtual:pwa-register/react";

import { SEVERITY_META } from "@/context/NotificationContext";

import { applyPwaUpdate } from "./applyPwaUpdate";

const INFO = SEVERITY_META.info;

const btnBase = {
  textTransform: "none",
  fontWeight: 600,
  minHeight: 40,
  px: 1.5,
  borderRadius: "8px",
  whiteSpace: "nowrap",
};

/**
 * Non-blocking prompt when a new app build is available. Replaces PWA auto-reload on deploy.
 */
export function PwaUpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  if (!import.meta.env.PROD) {
    return null;
  }

  return (
    <Snackbar
      open={needRefresh}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      sx={{
        bottom: { xs: "max(16px, env(safe-area-inset-bottom))", sm: 24 },
        px: { xs: 2, sm: 0 },
      }}
    >
      <Alert
        severity="info"
        variant="standard"
        icon={
          <Box
            sx={{
              width: 31,
              height: 31,
              borderRadius: "999px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: INFO.iconBackground,
              color: INFO.iconColor,
              flexShrink: 0,
            }}
          >
            <RefreshCw size={16} strokeWidth={2.25} aria-hidden />
          </Box>
        }
        sx={{
          width: { xs: "100%", sm: "min(540px, calc(100vw - 48px))" },
          maxWidth: "100%",
          borderRadius: "13px",
          border: `1px solid ${INFO.borderColor}`,
          borderLeft: `4px solid ${INFO.railColor}`,
          boxShadow: INFO.shadow,
          background: INFO.surface,
          color: "var(--foreground, #2D3748)",
          alignItems: "center",
          py: 1.25,
          pl: { xs: 1.25, sm: 1.5 },
          pr: { xs: 1.25, sm: 1.5 },
          "& .MuiAlert-icon": {
            marginRight: "12px",
            padding: 0,
            opacity: 1,
          },
          "& .MuiAlert-message": {
            width: "100%",
            padding: 0,
            overflow: "visible",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 1.25, sm: 2 },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "0.92rem",
                letterSpacing: "0.01em",
                fontWeight: 600,
                color: "var(--gray-500, #555555)",
                lineHeight: 1.25,
              }}
            >
              Update available
            </Typography>
            <Typography
              sx={{
                fontSize: "1.05rem",
                fontWeight: 500,
                color: "var(--foreground, #2D3748)",
                lineHeight: 1.4,
              }}
            >
              Refresh to load the latest horAIzen.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.75,
              flexShrink: 0,
            }}
          >
            <Button
              size="small"
              onClick={() => setNeedRefresh(false)}
              sx={{
                ...btnBase,
                color: "var(--gray-500, #555555)",
                "&:hover": { backgroundColor: "var(--primary-10)" },
              }}
            >
              Later
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => {
                void applyPwaUpdate(updateServiceWorker);
              }}
              sx={{
                ...btnBase,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Alert>
    </Snackbar>
  );
}
